import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Drawer,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { faxFilingsApi } from '../../services/api';

type SplitHint = {
  startPage: number;
  endPage: number;
  extractedName?: string;
  extractedDob?: string;
  pageCount?: number;
};

type Filing = {
  id: string;
  filename?: string;
  extractedName?: string;
  extractedDob?: string;
  matchReason?: string;
  patientName?: string;
  internalId?: string;
  documentId?: string;
  filedAt?: string;
  createdAt?: string;
  pageStart?: number | null;
  pageEnd?: number | null;
  pageCount?: number | null;
  suspectedPatients?: number;
  uncertainPageCount?: number;
  splitConfidence?: string | null;
  suggestedSplits?: SplitHint[];
};

type Page = {
  index: number;
  widthPt: number;
  heightPt: number;
  imageDataUrl: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const PageThumb: React.FC<{ page: Page }> = ({ page }) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [lens, setLens] = useState<{ x: number; y: number; show: boolean }>({ x: 0, y: 0, show: false });

  return (
    <Box
      ref={wrapRef}
      onMouseMove={(e) => {
        const r = wrapRef.current?.getBoundingClientRect();
        if (!r) return;
        setLens({
          x: ((e.clientX - r.left) / r.width) * 100,
          y: ((e.clientY - r.top) / r.height) * 100,
          show: true,
        });
      }}
      onMouseLeave={() => setLens((s) => ({ ...s, show: false }))}
      sx={{
        width: 168,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'visible',
        bgcolor: '#fafafa',
        position: 'relative',
        cursor: 'zoom-in',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ px: 0.75, py: 0.25, display: 'block' }}>
        p.{page.index + 1}
      </Typography>
      <Box
        component="img"
        src={page.imageDataUrl}
        alt={`Fax page ${page.index + 1}`}
        sx={{ width: '100%', display: 'block' }}
      />
      {lens.show && (
        <Box
          sx={{
            position: 'absolute',
            left: '100%',
            top: 0,
            ml: 1,
            width: 300,
            height: 220,
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
            boxShadow: 4,
            zIndex: 30,
            pointerEvents: 'none',
            backgroundImage: `url(${page.imageDataUrl})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '280%',
            backgroundPosition: `${lens.x}% ${lens.y}%`,
            bgcolor: '#fff',
          }}
        />
      )}
    </Box>
  );
};

const FaxPreview: React.FC<{
  filingId: string;
  defaultOpen?: boolean;
  onMeta?: (meta: {
    suggestedSplits?: SplitHint[];
    suspectedPatients?: number;
    splitConfidence?: string | null;
    splitApplied?: boolean;
  }) => void;
}> = ({ filingId, defaultOpen, onMeta }) => {
  const [pages, setPages] = useState<Page[]>([]);
  const [open, setOpen] = useState(!!defaultOpen);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (pages.length || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await faxFilingsApi.preview(filingId);
      const data = res.data?.data || {};
      setPages(data.pages || []);
      if (onMeta) {
        onMeta({
          suggestedSplits: data.suggestedSplits || [],
          suspectedPatients: data.suspectedPatients || 0,
          splitConfidence: data.splitConfidence || null,
          splitApplied: !!data.splitApplied,
        });
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, filingId]);

  return (
    <Box sx={{ mt: 1 }}>
      <Button size="small" onClick={() => setOpen((v) => !v)} sx={{ textTransform: 'none', mb: 1, minHeight: 28 }}>
        {open ? 'Hide pages' : 'Show pages'}
      </Button>
      {open && loading && <Typography variant="caption" color="text.secondary">Loading pages…</Typography>}
      {open && error && <Typography color="error" variant="caption" display="block">{error}</Typography>}
      {open && pages.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, overflow: 'visible' }}>
          {pages.map((page) => (
            <PageThumb key={page.index} page={page} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const FilingCard: React.FC<{
  row: Filing;
  mode: 'hold' | 'filed';
  onFile?: (internalId: string) => Promise<void>;
  onOpenChart?: () => void;
  onSplitApplied?: () => void;
}> = ({ row, mode, onFile, onOpenChart, onSplitApplied }) => {
  const [pick, setPick] = useState(false);
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<any[]>([]);
  const [meta, setMeta] = useState<Partial<Filing>>({});
  const merged = { ...row, ...meta };
  const multi = (merged.suspectedPatients || 0) >= 2 || (merged.suggestedSplits || []).length >= 2;
  const pageLabel = row.pageStart && row.pageEnd
    ? `Pages ${row.pageStart}–${row.pageEnd}`
    : row.pageCount
      ? `${row.pageCount} page${row.pageCount === 1 ? '' : 's'}`
      : 'Fax';

  const runSearch = async (q: string) => {
    setSearch(q);
    if (q.trim().length < 2) {
      setHits([]);
      return;
    }
    try {
      const res = await faxFilingsApi.searchPatients(q.trim());
      setHits(res.data?.data || []);
    } catch {
      setHits([]);
    }
  };

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: multi ? 'warning.light' : 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {mode === 'filed' ? (merged.patientName || merged.extractedName || 'Patient') : (merged.extractedName || merged.filename || 'Fax')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {pageLabel}
            {merged.extractedDob ? ` · DOB ${merged.extractedDob}` : ''}
            {merged.matchReason ? ` · ${merged.matchReason}` : ''}
            {mode === 'filed' && merged.filedAt ? ` · ${String(merged.filedAt).slice(0, 10)}` : ''}
          </Typography>
        </Box>
        {multi && mode === 'hold' && (
          <Chip size="small" color="warning" label="Splitting…" />
        )}
      </Box>

      <FaxPreview
        filingId={row.id}
        defaultOpen={mode === 'hold'}
        onMeta={(next) => {
          if (next.splitApplied && onSplitApplied) onSplitApplied();
          else setMeta((prev) => ({ ...prev, ...next }));
        }}
      />

      {mode === 'hold' && (
        <Box sx={{ mt: 1.25 }}>
          <Button size="small" variant="outlined" onClick={() => { setPick((v) => !v); setSearch(''); setHits([]); }} sx={{ textTransform: 'none' }}>
            Choose patient and file
          </Button>
          {pick && (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Search patient"
                value={search}
                onChange={(e) => runSearch(e.target.value)}
                sx={{ mb: 1 }}
              />
              {hits.map((c) => (
                <ListItemButton
                  key={c.internalId || c.id}
                  onClick={async () => { if (onFile) await onFile(c.internalId); }}
                  sx={{ borderRadius: 1, mb: 0.25 }}
                >
                  <ListItemText primary={c.name} secondary={`${c.dateOfBirth || ''} · ${c.internalId || ''}`} />
                </ListItemButton>
              ))}
            </Box>
          )}
        </Box>
      )}

      {mode === 'filed' && onOpenChart && (
        <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={onOpenChart}>
          Open chart and document
        </Button>
      )}
    </Box>
  );
};

const FaxesFiledDrawer: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [hold, setHold] = useState<Filing[]>([]);
  const [filed, setFiled] = useState<Filing[]>([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await faxFilingsApi.queue();
      setHold(res.data?.data?.hold || []);
      setFiled(res.data?.data?.filed || []);
      setDays(res.data?.data?.rotationDays || 7);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const openChart = (row: Filing) => {
    if (!row.internalId) return;
    navigate('/contacts', {
      state: {
        selectInternalId: row.internalId,
        selectDocId: row.documentId || null,
        selectTab: 'documents',
        returnTo: '/?faxFiled=1',
        backLabel: 'Back to Fax Filing',
      },
    });
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 560, md: 640 }, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>Fax Filing</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Each card is one fax packet. Hover a page for a magnifier. Multi-patient faxes split on their own. Just-filed stays on your list for {days} day{days === 1 ? '' : 's'}.
        </Typography>
        <Button size="small" onClick={load} sx={{ mb: 1.5 }}>Refresh</Button>
        {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}
        {loading && <Typography variant="body2">Loading…</Typography>}

        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Needs a person ({hold.length})</Typography>
        {hold.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Nothing waiting.</Typography>
        ) : hold.map((row) => (
          <FilingCard
            key={row.id}
            row={row}
            mode="hold"
            onFile={async (internalId) => {
              try {
                await faxFilingsApi.fileToPatient(row.id, internalId);
                await load();
              } catch (e: any) {
                setError(e?.response?.data?.error || e?.message || 'File failed');
              }
            }}
            onSplitApplied={load}
          />
        ))}

        <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>Just filed ({filed.length})</Typography>
        {filed.length === 0 ? (
          <Typography variant="body2" color="text.secondary">None in your current window.</Typography>
        ) : filed.map((row) => (
          <FilingCard
            key={row.id}
            row={row}
            mode="filed"
            onOpenChart={() => openChart(row)}
          />
        ))}
      </Box>
    </Drawer>
  );
};

export default FaxesFiledDrawer;
